import type { CollectionQueryParams } from "../utils/collectionQuery";
import type { ResourceRef } from "./recordRef";

/** Query params for record collection GET table mode / POST `/query`. */
export type RecordCollectionParams = CollectionQueryParams;

/**
 * Single hierarchical query-key factory for every record-service entity.
 *
 * Each builder is keyed on `(ref, …)` so the *same* invalidation logic works
 * for `Material`, `Release`, or any tenant-defined schema. Two flavors are
 * provided per scope:
 *
 *   - `<scope>(ref, …, tenantId)` — full key, suitable as a `queryKey`.
 *   - `<scope>Prefix(ref, …)`     — prefix key (no tenantId), used with
 *     `queryClient.invalidateQueries({ queryKey })` to invalidate every cache
 *     entry for that scope regardless of tenant.
 *
 * Keys are array-shaped so they compose cleanly with TanStack Query's
 * partial-match invalidation.
 */
export const recordKeys = {
  all: ["business-objects"] as const,

  /** Every collection query of every resource — for a write whose reach the FE cannot name. */
  collectionsAll: () => [...recordKeys.all, "collection"] as const,

  collectionPrefix: (ref: ResourceRef) =>
    [...recordKeys.collectionsAll(), ref.resource, ref.version] as const,

  collection: (ref: ResourceRef, tenantId?: string | null, params?: RecordCollectionParams) =>
    [...recordKeys.collectionPrefix(ref), tenantId, params ?? {}] as const,

  detailPrefix: (ref: ResourceRef, objectId: string) =>
    [...recordKeys.all, "detail", ref.resource, ref.version, objectId] as const,

  detail: (ref: ResourceRef, objectId: string, tenantId?: string | null) =>
    [...recordKeys.detailPrefix(ref, objectId), tenantId] as const,

  relationshipsPrefix: (ref: ResourceRef, objectId: string) =>
    [...recordKeys.all, "relationships", ref.resource, ref.version, objectId] as const,

  relationships: (ref: ResourceRef, objectId: string, tenantId?: string | null) =>
    [...recordKeys.relationshipsPrefix(ref, objectId), tenantId] as const,

  dereferencePrefix: (ref: ResourceRef, objectId: string) =>
    [...recordKeys.all, "dereference", ref.resource, ref.version, objectId] as const,

  dereference: (ref: ResourceRef, objectId: string, depth?: number, tenantId?: string | null) =>
    [...recordKeys.dereferencePrefix(ref, objectId), depth ?? null, tenantId] as const,
} as const;
